import MuiAlert, { type AlertColor } from '@mui/material/Alert';
import Portal from '@mui/material/Portal';
import Snackbar, { type SnackbarCloseReason } from '@mui/material/Snackbar';
import type { SxProps, Theme } from '@mui/material/styles';
import type React from 'react';
import {
  createContext,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { registerSnackbarCallback } from '../services/api';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
  duration: number;
  customStyle: SxProps<Theme> | null;
}

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: AlertColor,
    duration?: number,
    customStyle?: SxProps<Theme> | null
  ) => void;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
    duration: 2000,
    customStyle: null,
  });

  const showSnackbar = useCallback(
    (
      message: string,
      severity: AlertColor = 'info',
      duration: number = 2000,
      customStyle: SxProps<Theme> | null = null
    ): void => {
      setSnackbar({ open: true, message, severity, duration, customStyle });
    },
    []
  );

  useEffect(() => {
    registerSnackbarCallback(showSnackbar);
  }, [showSnackbar]);

  const handleClose = (_event?: SyntheticEvent | Event, reason?: SnackbarCloseReason): void => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.duration}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={handleClose}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              ...snackbar.customStyle,
            }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Portal>
    </SnackbarContext.Provider>
  );
};
